import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ApiClient } from "../core/api-client";
import type { PublicRuntimeConfig, SiteExperience } from "../core/site-experience";
import type { Locale, SiteId } from "../core/types";

interface AppContextValue {
  experience: SiteExperience;
  runtimeConfig: PublicRuntimeConfig;
  api: ApiClient;
  bootstrapSiteId?: SiteId;
  viewer: {
    siteId: SiteId;
    /** The signed-in account; scopes per-account browser state (the RPC explorer's inputs). */
    accountId?: string;
    displayName?: string;
    email?: string;
    emailVerified: boolean;
  } | null;
  accountRole: "owner" | "platform_admin" | null;
  /** Bootstrap admin surface (presentation-only; the server re-authorizes
   *  every admin procedure). null for non-admin viewers. */
  admin: { capabilities: string[] } | null;
  locale: Locale;
  setLocale(locale: Locale): void;
  loading: boolean;
  bootstrapError: unknown;
  refreshBootstrap(): Promise<unknown>;
  signInWithGoogle(): Promise<void>;
  signInWithPassword(email: string, password: string): Promise<void>;
  registerWithPassword(input: { name: string; email: string; password: string }): Promise<void>;
  sendPasswordReset(email: string): Promise<void>;
  resendVerification(): Promise<void>;
  /** Reload the Firebase user + re-exchange; resolves the new verified state. */
  refreshVerification(): Promise<boolean>;
  /** Exchange an already-obtained Firebase ID token (action handler flows). */
  exchangeIdToken(idToken: string): Promise<void>;
  revokeOtherSessions(): Promise<number>;
  /**
   * Account-boundary treatment for a session minted OUTSIDE exchangeIdToken
   * (the /auth/action reset flow exchanges first, revokes, then adopts):
   * drop every cached tenant answer and re-read the bootstrap.
   */
  adoptSession(): Promise<void>;
  signOut(): Promise<void>;
}

const AppContext = createContext<AppContextValue | null>(null);

const localeKey = (siteId: SiteId) => `mfa.v5.locale.${siteId}`;

/**
 * The locale this browser remembers for the site, or null. A browser that blocks site data
 * throws on any touch of localStorage: that reads as nothing remembered.
 */
function rememberedLocale(siteId: SiteId): string | null {
  try {
    return localStorage.getItem(localeKey(siteId));
  } catch {
    return null;
  }
}

/** Remembers the locale for the site; with site data blocked the choice lasts for this page. */
function rememberLocale(siteId: SiteId, locale: Locale): void {
  try {
    localStorage.setItem(localeKey(siteId), locale);
  } catch {
    // Site data is blocked: nothing to persist to.
  }
}

function chooseInitialLocale(experience: SiteExperience): Locale {
  const stored = rememberedLocale(experience.id);
  if (stored === "en" || stored === "es") return stored;
  return navigator.language.toLowerCase().startsWith("es") ? "es" : experience.defaultLocale;
}

export function AppContextProvider({
  experience,
  runtimeConfig,
  children,
}: {
  experience: SiteExperience;
  runtimeConfig: PublicRuntimeConfig;
  children: ReactNode;
}) {
  const api = useMemo(
    () => new ApiClient(runtimeConfig.apiBaseUrl ?? import.meta.env.VITE_API_BASE_URL ?? "/api/v5"),
    [runtimeConfig.apiBaseUrl],
  );
  const [locale, setLocaleState] = useState<Locale>(() => chooseInitialLocale(experience));
  const queryClient = useQueryClient();
  const bootstrapQuery = useQuery({
    queryKey: ["bootstrap", experience.id],
    queryFn: () => api.getBootstrap(),
    retry: false,
    staleTime: 30_000,
  });

  const setLocale = useCallback(
    (nextLocale: Locale) => {
      setLocaleState(nextLocale);
      rememberLocale(experience.id, nextLocale);
      if (bootstrapQuery.data?.authenticated) {
        void api
          .updateAccountPreferences(nextLocale)
          .then(() => queryClient.invalidateQueries({ queryKey: ["bootstrap", experience.id] }))
          .catch((error) => console.error("Unable to persist the account locale", error));
      }
    },
    [api, bootstrapQuery.data?.authenticated, experience.id, queryClient],
  );

  useEffect(() => {
    const serverLocale = bootstrapQuery.data?.account?.preferredLocale;
    if (serverLocale && !rememberedLocale(experience.id)) {
      setLocaleState(serverLocale);
    }
  }, [bootstrapQuery.data?.account?.preferredLocale, experience.id]);

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dataset.site = experience.id;
    document.documentElement.dataset.theme = experience.themeId;
    document.title = experience.brand.name;
  }, [experience, locale]);

  const refreshBootstrap = useCallback(
    () => queryClient.invalidateQueries({ queryKey: ["bootstrap", experience.id] }),
    [experience.id, queryClient],
  );

  useEffect(() => {
    // Any product call answering 401 means the session died server-side. Drop
    // ALL cached tenant data (not just bootstrap) so a subsequent sign-in as a
    // different account in this tab cannot render the previous account's
    // projects or billing from stale cache.
    api.onUnauthorized = () => {
      queryClient.clear();
    };
    return () => {
      api.onUnauthorized = null;
    };
  }, [api, queryClient]);

  const exchangeToken = useCallback(
    async (idToken: string, options?: { sameAccount?: boolean }) => {
      const previousPrincipalId = bootstrapQuery.data?.principal?.id;
      const response = await api.exchangeAuth(idToken, locale);
      // Trust the server's verdict, not the caller's intent: "same account"
      // stays soft only when the minted session's principal is the one we were
      // already rendering. A diverged live Firebase session (e.g. an
      // /auth/action reset for another account whose exchange failed) crosses
      // an account boundary here and must not keep the previous account's cache.
      if (
        options?.sameAccount &&
        previousPrincipalId != null &&
        response.principal?.id === previousPrincipalId
      ) {
        // Re-exchanging the SAME principal (e.g. the verification re-check):
        // keep the cache and refetch in the background, so the member tree
        // never blinks to the loading state and unmounts an open builder with
        // unsaved edits. refetch() retains the prior data while it fetches.
        await bootstrapQuery.refetch();
        return response;
      }
      // New account boundary: clear any cache belonging to a previous session in
      // this tab before loading the new account's data. Then REFETCH — after
      // clear() there is no cached query left for invalidateQueries to match,
      // so only the observer's own refetch actually reloads the bootstrap.
      queryClient.clear();
      await bootstrapQuery.refetch();
      return response;
    },
    [api, bootstrapQuery, locale, queryClient],
  );

  const signInWithGoogle = useCallback(async () => {
    const { getGoogleIdToken } = await import("../auth/firebase-auth");
    const token = await getGoogleIdToken(runtimeConfig);
    await exchangeToken(token);
  }, [exchangeToken, runtimeConfig]);

  const signInWithPassword = useCallback(
    async (email: string, password: string) => {
      const firebase = await import("../auth/firebase-auth");
      const token = await firebase.signInWithPassword(runtimeConfig, email, password);
      await exchangeToken(token);
    },
    [exchangeToken, runtimeConfig],
  );

  const registerWithPassword = useCallback(
    async (input: { name: string; email: string; password: string }) => {
      const firebase = await import("../auth/firebase-auth");
      const token = await firebase.registerWithPassword(runtimeConfig, locale, input);
      await exchangeToken(token);
    },
    [exchangeToken, locale, runtimeConfig],
  );

  const sendPasswordReset = useCallback(
    async (email: string) => {
      const firebase = await import("../auth/firebase-auth");
      await firebase.sendPasswordReset(runtimeConfig, locale, email);
    },
    [locale, runtimeConfig],
  );

  const resendVerification = useCallback(async () => {
    const { resendVerificationEmail } = await import("../auth/firebase-auth");
    await resendVerificationEmail(runtimeConfig, locale);
  }, [locale, runtimeConfig]);

  const refreshVerification = useCallback(async () => {
    const { refreshVerifiedIdToken } = await import("../auth/firebase-auth");
    const token = await refreshVerifiedIdToken(runtimeConfig);
    // Soft refresh so the member tree stays mounted — exchangeToken still
    // hard-clears if the minted principal turns out not to be the viewer.
    const response = await exchangeToken(token, { sameAccount: true });
    return response.principal?.emailVerified ?? false;
  }, [exchangeToken, runtimeConfig]);

  const exchangeIdToken = useCallback(
    async (idToken: string) => {
      await exchangeToken(idToken);
    },
    [exchangeToken],
  );

  const revokeOtherSessions = useCallback(async () => {
    const { revokedSessions } = await api.revokeOtherSessions();
    return revokedSessions;
  }, [api]);

  const adoptSession = useCallback(async () => {
    queryClient.clear();
    await bootstrapQuery.refetch();
  }, [bootstrapQuery, queryClient]);

  const signOut = useCallback(async () => {
    // Finally-safe: whatever fails, the local session artifacts must go —
    // tenant caches are cleared and bootstrap re-read even when the server
    // logout or the Firebase sign-out throws. Failures are logged, not
    // rethrown: the refetched bootstrap is the honest signed-in/out signal.
    try {
      await api.logout();
    } catch (error) {
      console.error("Logout failed; local cleanup continues", error);
    } finally {
      try {
        const { signOutFirebase } = await import("../auth/firebase-auth");
        await signOutFirebase(runtimeConfig);
      } catch (error) {
        console.error("Firebase sign-out failed; local cleanup continues", error);
      } finally {
        queryClient.clear();
        await bootstrapQuery.refetch();
      }
    }
  }, [api, bootstrapQuery, queryClient, runtimeConfig]);

  const value = useMemo<AppContextValue>(
    () => ({
      experience,
      runtimeConfig,
      api,
      bootstrapSiteId: bootstrapQuery.data?.site.id,
      viewer:
        bootstrapQuery.data?.authenticated && bootstrapQuery.data.principal
          ? {
              siteId: bootstrapQuery.data.site.id,
              ...(bootstrapQuery.data.account ? { accountId: bootstrapQuery.data.account.id } : {}),
              displayName: bootstrapQuery.data.principal.displayName,
              emailVerified: bootstrapQuery.data.principal.emailVerified,
              ...(bootstrapQuery.data.principal.email
                ? { email: bootstrapQuery.data.principal.email }
                : {}),
            }
          : null,
      accountRole: bootstrapQuery.data?.account?.role ?? null,
      admin: bootstrapQuery.data?.admin ?? null,
      locale,
      setLocale,
      loading: bootstrapQuery.isLoading,
      bootstrapError: bootstrapQuery.error,
      refreshBootstrap,
      signInWithGoogle,
      signInWithPassword,
      registerWithPassword,
      sendPasswordReset,
      resendVerification,
      refreshVerification,
      exchangeIdToken,
      revokeOtherSessions,
      adoptSession,
      signOut,
    }),
    [
      adoptSession,
      api,
      bootstrapQuery.data,
      bootstrapQuery.error,
      bootstrapQuery.isLoading,
      exchangeIdToken,
      experience,
      locale,
      refreshBootstrap,
      refreshVerification,
      registerWithPassword,
      resendVerification,
      revokeOtherSessions,
      runtimeConfig,
      sendPasswordReset,
      setLocale,
      signInWithGoogle,
      signInWithPassword,
      signOut,
    ],
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useAppContext(): AppContextValue {
  const value = useContext(AppContext);
  if (!value) throw new Error("useAppContext must be used within AppContextProvider");
  return value;
}
